import { CalculatedFees } from '@/apis/payments/requests'
import { PaymentSummary } from '@/app/common/components/purse_allocation_form/payment_summary'
import { PurseAllocationForm } from '@/app/common/components/purse_allocation_form/purse_allocation_form'
import { PurseAllocationSummary } from '@/app/common/components/purse_allocation_form/purse_allocation_summary'
import usePurseAllocationForm from '@/app/common/components/purse_allocation_form/usePurseAllocationForm'
import { Button } from '@/common/components/Button'
import { PursesArray } from '@/schemas/provider.schema'
import { SelectedPurse } from '@/storage/applications/providerPaySlice'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import FamilyWizardLayout from '../../../family_application/FamilyWizardLayout'
import { SubmitButton } from '../../../family_application/student_details/shared/submit-button'
import { createPurseFormSchema } from './choose_purses.schema'

interface PurseSelectionFormProps {
  selectedPurses: SelectedPurse[]
  apiPurses: PursesArray
  onSubmit: (data: PurseFormData) => void
  onBack: () => void
  submitText?: string
  serviceFeeInformation: CalculatedFees
}

export type PurseFormData = z.infer<ReturnType<typeof createPurseFormSchema>>

export const PurseSelectionForm = ({
  selectedPurses,
  apiPurses,
  onSubmit,
  onBack,
  serviceFeeInformation,
  submitText = 'next',
}: PurseSelectionFormProps) => {
  const { t: translate } = useTranslation()
  const { methods, getSelectedPurses, totalApplicantAmount } =
    usePurseAllocationForm({
      apiPurses,
      baseAmount: serviceFeeInformation.base_amount,
      totalAmount: serviceFeeInformation.net_amount,
      serviceFee: serviceFeeInformation.service_fee,
      serviceFeePayer: serviceFeeInformation.serviceFeePayer,
      selectedPurses,
    })

  const onFormSubmit = (data: PurseFormData) => {
    onSubmit(data)
  }

  const allowedPurses = apiPurses.filter((purse) => purse.is_funded)

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1 className="mb-4 max-w-lg">
          {translate('choose_which_purse_will_fund_transaction')}
        </h1>
      </FamilyWizardLayout.Header>
      <form onSubmit={methods.handleSubmit(onFormSubmit)}>
        <FamilyWizardLayout.Content className="space-y-4">
          <p className="font-normal text-center">
            {translate('purse_would_you_like')}
          </p>
          <div className="space-y-4">
            <PaymentSummary
              baseAmount={serviceFeeInformation.base_amount}
              serviceFeePayer={serviceFeeInformation.serviceFeePayer}
              paymentAmount={totalApplicantAmount}
              serviceFeeAmount={serviceFeeInformation.service_fee}
            />
            <PurseAllocationForm
              methods={methods}
              paymentType={'Pay Provider'}
              apiPurses={allowedPurses}
              paymentAmount={totalApplicantAmount}
            />
            <PurseAllocationSummary
              selectedPurses={getSelectedPurses()}
              total={totalApplicantAmount}
            />
          </div>
        </FamilyWizardLayout.Content>
        <FamilyWizardLayout.ActionBar className="justify-center">
          <div className="flex gap-2">
            <Button variant="outline" onClick={onBack} className="px-10">
              {translate('back')}
            </Button>{' '}
            <SubmitButton
              status={'idle'}
              buttonTxt={translate(submitText)}
              className="px-10"
            />
          </div>
        </FamilyWizardLayout.ActionBar>
      </form>
    </FamilyWizardLayout>
  )
}
